import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import {
  getActivityColor,
  getActivityDescription,
  getActivityIcon,
  getActivityTargetPath,
} from '@/lib/activity-feed'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import { Activity } from '@/types/api'
import { useQuery } from '@tanstack/react-query'
import { Eye } from 'lucide-react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

interface ActivityFeedProps {
  className?: string
}

const ActivitySkeleton = () => (
  <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
    <div className="flex items-center space-x-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div>
        <Skeleton className="h-4 w-48 mb-1" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
    <Skeleton className="h-3 w-20" />
  </div>
)

export const ActivityFeed: React.FC<ActivityFeedProps> = ({ className }) => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['activities'],
    queryFn: () => apiClient.getActivities({ limit: 5 }),
  })

  const activities = response?.data?.activities || []

  const handleViewActivity = (activity: Activity) => {
    const targetPath = getActivityTargetPath(activity)
    if (targetPath) {
      navigate(targetPath)
    }
  }

  const handleViewAllActivities = () => {
    navigate('/activities')
  }

  if (error) {
    toast({
      title: 'Error',
      description: 'Failed to load recent activities',
      variant: 'destructive',
    })
  }

  return (
    <Card className={cn('rounded-3xl border-slate-200/80 bg-white shadow-sm', className)}>
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">Recent Activity</CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              The latest administrative activity across orders, content, and catalog operations.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={handleViewAllActivities}
          >
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
          {isLoading ? (
            <>
              <ActivitySkeleton />
              <ActivitySkeleton />
              <ActivitySkeleton />
            </>
          ) : activities.length > 0 ? (
            activities.map((activity) => {
              const targetPath = getActivityTargetPath(activity)

              return (
                <div
                  key={activity.id}
                  className={cn(
                    'flex items-center rounded-2xl border border-slate-200/80 p-4 transition-colors',
                    targetPath ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'
                  )}
                  onClick={() => handleViewActivity(activity)}
                >
                  <div className="flex min-w-0 flex-1 items-center space-x-3">
                    <div
                      className={cn(
                        'rounded-2xl p-2.5 flex-shrink-0',
                        getActivityColor(activity.action)
                      )}
                    >
                      {getActivityIcon(activity.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {getActivityDescription(activity)}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        <span>by {activity.admin.name}</span>
                        {activity.resource_type && <span>{activity.resource_type}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-500 whitespace-nowrap">
                        {activity.time_ago}
                      </p>
                    </div>
                    {targetPath && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewActivity(activity)
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-10 text-center text-slate-500">
              No recent activities found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
